import { Avatar, type AvatarProps } from "@outpave/ds";
import { useBrandingContext } from "context/BrandingContext";
import { getDomain, logoLink } from "./RenderMerchantDisplay";

export interface CompanyAvatarProps extends Omit<
  AvatarProps,
  "src" | "name" | "alt"
> {
  /** Company name, rendered as initials when no logo resolves. */
  name?: string;
  /** Company website, used to build the Brandfetch logo request. */
  website?: string;
}

/** Brandfetch request width, matched to the DS Avatar diameter. */
const REQUEST_WIDTH = { sm: 24, md: 28, lg: 36, xl: 44 } as const;

/**
 * The company mark: the runtime branding icon when one is set, else the
 * company's Brandfetch logo, else initials. Fell out of OUT-1133, which
 * retired admin's own CustomCompanyAvatar; the logo resolution is the only
 * part of it that was not the DS Avatar in disguise.
 */
const CompanyAvatar = ({
  name,
  website,
  size = "md",
  ...rest
}: CompanyAvatarProps) => {
  const { logos } = useBrandingContext();

  // getDomain returns "" for invalid input, which would build a URL that
  // Brandfetch answers with someone else's logo.
  const domain = website ? getDomain(website) : "";
  const src =
    logos.icon ||
    (domain
      ? logoLink(domain, "icon", "lettermark", "light", REQUEST_WIDTH[size])
      : undefined);

  return <Avatar src={src} name={name} alt={name} size={size} {...rest} />;
};

export default CompanyAvatar;
